import { useMutation } from '@apollo/client';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { AppNames } from '@openx/components-core/lib/PageNotFound/PageNotFound';
import { PageHeader } from '@openx/components-header/PageHeader';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateBidderCreativeDocument } from '../../graphql/mutation/creatives/CreateBidderCreative.generated';
import { useLocalErrorHandling } from '../graphql/useLocalErrorHandling';
import { CreativeFormFields } from './CreativeForm/CreativeFormFields';
import { getCreativeCreateInitialState, transformFormDataToMutationInput } from './CreativeForm/utils';
import type { CreativeFormState } from './CreativeForm/validation/validationSchema';
import { useFetchRequiredOptions } from './hooks/useFetchRequiredOptions';

export const CreateCreativePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { advertiserOptions, iabCategoryOptions, isLoading } = useFetchRequiredOptions({ includeIabCategories: true });
  const initialValues = getCreativeCreateInitialState(advertiserOptions);
  const [createBidderCreative] = useMutation(CreateBidderCreativeDocument);

  useLocalErrorHandling(CreateBidderCreativeDocument, () => true);

  const handleCreate = async (data: CreativeFormState): Promise<void> => {
    const input = transformFormDataToMutationInput(data);
    const result = await createBidderCreative({
      variables: { input },
      update: cache => {
        cache.evict({ fieldName: 'bidderCreatives' });
        cache.gc();
      },
    });

    if (result.data?.createBidderCreative) {
      enqueueSnackbar(t('Creative created successfully'), { variant: 'success' });
      navigate(`/aqua/creatives/${result.data.createBidderCreative.id}`);
    }
  };

  if (isLoading) {
    return (
      <PageHeader
        title={t('Create New Creative')}
        titlePrefix={AppNames.OpenXSelect}
        goBack={() => navigate('/aqua/creatives')}
      >
        <Loader />
      </PageHeader>
    );
  }

  return (
    <PageHeader
      title={t('Create New Creative')}
      titlePrefix={AppNames.OpenXSelect}
      goBack={() => navigate('/aqua/creatives')}
    >
      <CreativeFormFields
        advertiserOptions={advertiserOptions}
        iabCategoryOptions={iabCategoryOptions}
        initialValues={initialValues}
        onSubmit={handleCreate}
      />
    </PageHeader>
  );
};
